<template>
  <q-card flat bordered>
    <q-card-section class="flex justify-between">
      <div>
        <div class="text-h6">Bulk Rental Property Data</div>
        <div style="font-size: 12px">Manage your bulk rental property data subscriptions.</div>
      </div>
      <div class="q-my-auto">
        <q-btn icon="refresh" flat unelevated @click="refreshSubscriptions" />
      </div>
    </q-card-section>

    <q-markup-table v-if="loading" dense>
      <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
        <tr>
          <th v-for="n in 3" :key="n" scope="col">
            <q-skeleton animation="blink" type="text" />
          </th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="n in 3" :key="n">
          <td style="width: 32.9%">
            <q-skeleton animation="blink" type="text" />
          </td>
          <td style="width: 12.9%">
            <q-skeleton animation="blink" type="text" />
          </td>
          <td style="width: 19%">
            <q-skeleton animation="blink" type="text" />
          </td>
          <td class="row justify-end" style="height: 45px">
            <q-skeleton animation="blink" type="text" style="width: 125px" />
          </td>
        </tr>
      </tbody>
    </q-markup-table>
    <q-table
      v-else
      color="primary"
      dense
      bordered
      row-key="id"
      :rows="rows"
      :columns="columns"
      :pagination="{
        rowsPerPage: 10
      }"
    >
      <template #header="props">
        <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
          <q-th v-for="col in props.cols" :key="col.name" :props="props">
            {{ col.label }}
          </q-th>
          <q-th></q-th>
        </q-tr>
      </template>
      <template #body="props">
        <q-tr
          :props="props"
          @dblclick="
            $router.push({
              name: 'Edit Bulk Rental Property Data',
              params: { bulkDataId: props.row.id }
            })
          "
        >
          <q-td v-for="(col, colIndex) in props.cols" :key="col.name" :props="props">
            <router-link
              v-if="colIndex === 0"
              :to="{ name: 'Edit Bulk Rental Property Data', params: { bulkDataId: props.row.id } }"
              class="text-primary"
            >
              {{ col.value }}
            </router-link>
            <template v-else>
              {{ col.value }}
            </template>
          </q-td>
          <q-td style="text-align: right">
            <q-btn
              round
              dense
              unelevated
              :disable="!props.row.emails_enabled"
              :loading="getForceRunLoading(props.row.id)"
              text-color="primary"
              @click="forceRun(props.row.id)"
            >
              <q-icon name="mail_outline" size="medium" color="primary"></q-icon>
              <q-tooltip v-if="!props.row.emails_enabled">Emails Disabled</q-tooltip>
              <q-tooltip v-else>Send Email</q-tooltip>
            </q-btn>
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </q-card>
</template>

<script lang="ts">
import { useBulkDataStore } from '@/stores/bulkdata'
import type { RentSourceBulkDataSubscriptionAttributes } from 'common'
import type { QTableColumn } from 'quasar'
import { formatDate } from '@/utils'

export default {
  name: 'BulkData',
  components: {},
  data() {
    return {
      loading: false,
      forceRunLoading: {} as { [id: number]: boolean }
    }
  },
  computed: {
    rows() {
      return useBulkDataStore().activeSubscriptions
    },
    columns() {
      const cols: QTableColumn[] = [
        {
          field: 'msa',
          name: 'msa',
          label: 'Name',
          sortable: true,
          align: 'left'
        }
      ]

      if (this.$q.screen.gt.xs) {
        cols.push({
          field: 'schedule',
          name: 'schedule',
          label: 'Schedule',
          sortable: true,
          align: 'left',
          format: (vals: string[], row: RentSourceBulkDataSubscriptionAttributes) => {
            if (!row.emails_enabled) {
              return ''
            }
            const abbrev = {
              Monday: 'Mon',
              Tuesday: 'Tue',
              Wednesday: 'Wed',
              Thursday: 'Thu',
              Friday: 'Fri',
              Saturday: 'Sat',
              Sunday: 'Sun'
            } as { [key: string]: string }
            return vals.map((_) => abbrev[_]).join(', ')
          }
        })
      }
      if (this.$q.screen.gt.sm) {
        cols.push({
          field: 'modified_on',
          name: 'modified_on',
          label: 'Last Modified',
          sortable: true,
          align: 'left',
          format: formatDate
        })
      }
      return cols
    }
  },
  async created() {
    if (!this.rows.length) {
      this.refreshSubscriptions()
    }
  },
  methods: {
    refreshSubscriptions() {
      this.loading = true
      useBulkDataStore()
        .refreshList()
        .finally(() => {
          this.loading = false
        })
    },
    getForceRunLoading(id: number) {
      return this.forceRunLoading[id]
    },
    forceRun(id: number) {
      this.forceRunLoading[id] = true
      useBulkDataStore()
        .sendEmail(id)
        .finally(() => {
          this.forceRunLoading[id] = false
        })
    }
  }
}
</script>
